import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  combineLatestWith,
  distinctUntilChanged,
  filter,
  Observable,
} from 'rxjs';
import { SubHeaderService } from 'src/app/shared/store/subheader/subheader.service';
import {
  CurrentUserModel,
  User,
  UserDetailModel,
} from 'src/app/shared/store/user/type';
import { UserService } from 'src/app/shared/store/user/user.service';
import { BreadcrumbService } from 'xng-breadcrumb';
import { Entity, View } from '../../../../../shared/store/view/types';
import { ViewService } from '../../../../../shared/store/view/views.service';
import { FormService } from '../../../../../shared/store/form/form.service';
import { DropdownStyle } from '../../../../../shared/store/typeahead/types';
import { map, take, tap } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';

@UntilDestroy()
@Component({
  selector: 'app-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss'],
  standalone: false,
})
export class UserEditComponent implements OnInit, OnDestroy {
  user$: Observable<UserDetailModel>;
  userForm: FormGroup;
  currentUser$: Observable<CurrentUserModel>;

  userId: string;

  view$: Observable<View>;

  imageUrl: string = '';
  file: File | undefined;

  mainSalesTeamDomain: string = 'id=0'; //fake domain to have no result on init

  protected readonly Entity = Entity;
  protected readonly DropdownStyle = DropdownStyle;

  constructor(
    private readonly userService: UserService,
    private readonly subHeaderService: SubHeaderService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly viewService: ViewService,
    private readonly formBuilder: FormBuilder,
    private readonly formService: FormService,
  ) {
    this.user$ = this.userService.getById.value$;
    this.view$ = this.viewService.getViews.value$;
    this.currentUser$ = this.userService.getCurrentUser.value$;
  }

  ngOnInit(): void {
    this.userId = this.route.snapshot.params['id'];
    this.userService.getById.call(this.route.snapshot.params['id']);

    //Form declaration
    this.userForm = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', Validators.required],
      contactId: [undefined],
      contactPictureId: [null],
      contactPictureFile: [undefined],
      salesPersonCommissionPlanId: [undefined],
      sharedCommissionPlanId: [undefined],
      salesTeamIds: [undefined],
      mainSalesTeamId: [undefined, [Validators.required]],
      groupId: [undefined],
      workScheduleId: [undefined],
      telephonyExtension: [undefined],
      // name: new FormControl('',[validators.required, validators.maxLength(80)]),
    });

    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'userUpdate') this.onSubmit();
        if (x == 'userDetails') {
          this.formService.clear();
          this.router.navigate(['Configuration/Users/Details/', this.userId]);
        }
        this.subHeaderService.setTriggerAction('');
      });

    this.user$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.userForm.patchValue({
          firstName: x.contactFirstName,
          lastName: x.contactLastName,
          email: x.aspNetUserEmail,
          contactId: x.contactId,
          telephonyExtension: x.telephonyExtension,
          contactPictureId: x.contactPictureId,
          salesPersonCommissionPlanId: x.salesPersonCommissionPlanId,
          sharedCommissionPlanId: x.sharedCommissionPlanId,
          salesTeamIds: x.userSalesTeams.map((x) => x.id),
          mainSalesTeamId: x.mainSalesTeamId,
          groupId: x.groupId,
          workScheduleId: x.workScheduleId,
        });
        this.breadcrumbService.set('@userEdit', x.contactName);
        if (x.contactPicturePath) {
          this.imageUrl = environment.comparanooapiurl + x.contactPicturePath;
        }
        //init the dynamic domain
        this.mainSalesTeamDomain = x.userSalesTeams
          .map((x) => 'id=' + x.id)
          .join('|');
        if (!this.mainSalesTeamDomain) this.mainSalesTeamDomain = 'id=0';
      });
    this.view$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@list', x.title);
      });

    this.userForm
      .get('salesTeamIds')
      ?.valueChanges.pipe(
        distinctUntilChanged(),
        untilDestroyed(this),
        map((ids: number[]) => {
          //if the main salesteam is removed in the list of the salesteam, remove it from main
          if (!ids.includes(this.userForm.get('mainSalesTeamId')?.value)) {
            this.userForm.get('mainSalesTeamId')?.setValue(undefined);
          }
          //create the dynamic domain
          return ids
            .map((id: number) => {
              return `id=${id}`;
            })
            .join('|');
        }),
        tap((domain) => {
          this.mainSalesTeamDomain = domain;
          if (!this.mainSalesTeamDomain) this.mainSalesTeamDomain = 'id=0';
        }),
        // tap((x) => console.log(x)),
      )
      .subscribe();

    this.currentUser$
      .pipe(
        combineLatestWith(this.view$),
        filter(
          ([currentUser, view]) =>
            !!currentUser && !!view && !view.canSeeOtherUsersData,
        ),
        take(1),
      )
      .subscribe(([currentUser, view]) => {
        if (currentUser.id !== +this.userId) {
          this.router.navigate(['Configuration/Users/']);
        }
      });
  }

  onSubmit() {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      this.formService.countErrors(this.userForm, true);
      return;
    }

    let user: User = {
      firstName: this.userForm.value.firstName!,
      lastName: this.userForm.value.lastName!,
      email: this.userForm.value.email!,

      id: +this.userId,
      active: this.userForm.value.active!,
      contactId: this.userForm.value.contactId!,

      //avatar
      contactPictureId: this.userForm.value.contactPictureId!,
      contactPictureFile: this.file,

      //commission
      salesPersonCommissionPlanId:
        this.userForm.value.salesPersonCommissionPlanId!,
      sharedCommissionPlanId: this.userForm.value.sharedCommissionPlanId!,

      //salesteams
      salesTeamIds: this.userForm.value.salesTeamIds!,
      mainSalesTeamId: this.userForm.value.mainSalesTeamId!,

      // Poste téléphonique : clé de rattachement des appels au commercial
      telephonyExtension: this.userForm.value.telephonyExtension!,

      //group
      groupId: this.userForm.value.groupId!,
      groups: [],

      workScheduleId: this.userForm.value.workScheduleId!,

      //Others
      contactName: '',
      contactFirstName: '',
      contactLastName: '',
      contactPicturePath: '',
      aspNetUserEmail: '',
      aspNetUserUserName: '',
      aspNetUserEmailConfirmed: false,
      userSalesTeams: [],
    };
    let result$ = this.userService.update.call(user);

    result$
      .pipe(
        combineLatestWith(this.currentUser$),
        filter(([result, currentUser]) => !!result && !!currentUser),
        take(1),
      )
      .subscribe(([result, currentUser]) => {
        this.formService.clear();

        if (result.id == currentUser.id) {
          this.userService.getCurrentUser.call();
        }

        this.router.navigate(['Configuration/Users/Details/', result.id]);
      });
  }

  ngOnDestroy(): void {
    this.userService.update.reset();
    this.breadcrumbService.set('@userDetails', ' ');
  }

  onAvatarChange(file: File | undefined) {
    if (file) {
      this.file = file;
    } else if (this.file) {
      // Cross after picking a new file: cancel the pick, keep the saved picture
      this.file = undefined;
    } else {
      // Cross on the saved picture: remove it
      this.userForm.patchValue({ contactPictureId: null });
    }
  }
}
